import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock,
  Users,
  MapPin,
  Check,
  Calendar,
  ArrowLeft,
  Mountain,
  Car,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { fr, enUS, ar, nl, es } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const locales = { fr, en: enUS, ar, nl, es };

const CircuitDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [circuit, setCircuit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    user_name: "",
    user_email: "",
    user_phone: "",
    num_travelers: 2,
    special_requests: "",
  });

  useEffect(() => {
    const fetchCircuit = async () => {
      try {
        const response = await axios.get(`${API}/circuits/${id}`);
        setCircuit(response.data);
      } catch (error) {
        console.error("Error fetching circuit:", error);
        toast.error(t("common.error"));
      } finally {
        setLoading(false);
      }
    };
    fetchCircuit();
  }, [id, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error(lang === "fr" ? "Veuillez sélectionner une date" : "Please select a date");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/reservations`, {
        ...formData,
        circuit_id: id,
        preferred_date: format(date, "yyyy-MM-dd"),
        language: lang,
      });

      if (response.data.id) {
        navigate(`/checkout/${response.data.id}`);
      }
    } catch (error) {
      console.error("Error creating reservation:", error);
      toast.error(t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "easy":
        return "bg-green-100 text-green-800";
      case "moderate":
        return "bg-yellow-100 text-yellow-800";
      case "challenging":
        return "bg-red-100 text-red-800";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!circuit) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-muted-foreground">{t("common.error")}</p>
      </div>
    );
  }

  const totalPrice = circuit.price * formData.num_travelers;

  return (
    <div className="min-h-screen pt-20" data-testid="circuit-detail-page">
      {/* Hero Image */}
      <div className="relative h-[50vh] md:h-[60vh]">
        <img
          src={circuit.gallery[selectedImage] || circuit.image_url}
          alt={circuit.title[lang] || circuit.title.fr}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />

        {/* Back Button */}
        <Button
          variant="ghost"
          className="absolute top-24 left-4 md:left-8 text-white hover:bg-white/20"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          {t("common.back")}
        </Button>

        {/* Title */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Badge className={circuit.type === "4x4" ? "bg-accent" : "bg-primary"}>
                {circuit.type === "4x4" ? <Car className="h-3 w-3 mr-1" /> : <Mountain className="h-3 w-3 mr-1" />}
                {circuit.type}
              </Badge>
              <Badge className={getDifficultyColor(circuit.difficulty)}>
                {t(`circuits.difficulty.${circuit.difficulty}`)}
              </Badge>
            </div>
            <h1 className="font-serif text-3xl md:text-5xl font-bold text-white">
              {circuit.title[lang] || circuit.title.fr}
            </h1>
          </div>
        </div>
      </div>

      {/* Gallery Thumbnails */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 -mt-6 relative z-10">
        <div className="flex gap-3 overflow-x-auto pb-4">
          {circuit.gallery.map((img, index) => (
            <button
              key={index}
              onClick={() => setSelectedImage(index)}
              className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                selectedImage === index ? "border-primary" : "border-transparent"
              }`}
              data-testid={`gallery-thumb-${index}`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {circuit.description[lang] || circuit.description.fr}
              </p>
            </div>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-6 py-6 border-y border-border">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                <span className="font-semibold">{circuit.duration_days} {t("circuits.duration")}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <span className="font-semibold">{t("circuits.maxGroup")}: {circuit.max_group_size} {t("circuits.persons")}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                <span className="font-semibold">{t("circuits.departure")}: {circuit.departure_city}</span>
              </div>
            </div>

            {/* Highlights */}
            <div>
              <h2 className="font-serif text-2xl font-semibold mb-4">{t("circuits.highlights")}</h2>
              <div className="grid grid-cols-2 gap-3">
                {(circuit.highlights?.[lang] || circuit.highlights?.fr || []).map((highlight, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-primary" />
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Includes */}
            <div>
              <h2 className="font-serif text-2xl font-semibold mb-4">{t("circuits.includes")}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(circuit.includes?.[lang] || circuit.includes?.fr || []).map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                    <Check className="h-5 w-5 text-green-600" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Itinerary */}
            <div>
              <h2 className="font-serif text-2xl font-semibold mb-6">{t("circuits.itinerary")}</h2>
              <div className="space-y-4">
                {(circuit.itinerary || []).map((day, index) => (
                  <div
                    key={index}
                    className="flex gap-4 p-4 bg-card rounded-xl border border-border"
                    data-testid={`itinerary-day-${index + 1}`}
                  >
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-bold">
                        {t("circuits.day")} {day.day}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">
                        {typeof day.title === "object" ? (day.title[lang] || day.title.fr || "") : day.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {typeof day.description === "object" ? (day.description[lang] || day.description.fr || "") : day.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar - Booking */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-card rounded-2xl p-6 shadow-lg border border-border">
              <div className="mb-6">
                <span className="text-muted-foreground">{t("circuits.from")}</span>
                <p className="text-4xl font-bold text-foreground">
                  €{circuit.price}
                  <span className="text-lg font-normal text-muted-foreground">/{t("circuits.perPerson")}</span>
                </p>
              </div>

              <form onSubmit={handleBooking} className="space-y-4">
                <div>
                  <Label htmlFor="user_name">{t("contact.name")}</Label>
                  <Input
                    id="user_name"
                    name="user_name"
                    value={formData.user_name}
                    onChange={handleInputChange}
                    required
                    data-testid="booking-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="user_email">{t("contact.email")}</Label>
                  <Input
                    id="user_email"
                    name="user_email"
                    type="email"
                    value={formData.user_email}
                    onChange={handleInputChange}
                    required
                    data-testid="booking-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="user_phone">{t("contact.phone")}</Label>
                  <Input
                    id="user_phone"
                    name="user_phone"
                    type="tel"
                    value={formData.user_phone}
                    onChange={handleInputChange}
                    required
                    data-testid="booking-phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="num_travelers">{t("booking.participants")}</Label>
                  <Input
                    id="num_travelers"
                    name="num_travelers"
                    type="number"
                    min="1"
                    max={circuit.max_group_size}
                    value={formData.num_travelers}
                    onChange={handleInputChange}
                    required
                    data-testid="booking-participants-input"
                  />
                </div>

                <div>
                  <Label>{t("booking.date")}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="booking-date-picker"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP", { locale: locales[lang] || locales.fr }) : lang === "fr" ? "Sélectionner une date" : "Select a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(date) => date < new Date()}
                        locale={locales[lang] || locales.fr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label htmlFor="special_requests">{t("booking.specialRequests")}</Label>
                  <Textarea
                    id="special_requests"
                    name="special_requests"
                    value={formData.special_requests}
                    onChange={handleInputChange}
                    rows={3}
                    data-testid="booking-requests-input"
                  />
                </div>

                <div className="pt-4 border-t border-border">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-semibold">{t("booking.total")}</span>
                    <span className="text-2xl font-bold text-primary">€{totalPrice.toFixed(2)}</span>
                  </div>
                  <Button
                    type="submit"
                    className="w-full rounded-full bg-primary hover:bg-primary/90 py-6 text-lg"
                    disabled={submitting}
                    data-testid="booking-submit-btn"
                  >
                    {submitting ? t("common.loading") : t("circuits.bookNow")}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CircuitDetailPage;
